import React, { createContext, useEffect, useState } from "react";
import MyBookingPageNavigation from "./component/MyBookingNavigation";
import BookingContent from "./component/BookingContent";
import { MyBookingWrapper } from "./styled/DesStyled";
import useLoadedUser from "../../hooks/auth/useLoadedUser";
import { UserRole } from "../../../models/User";
import BookingFilter from "./component/BookingFilter";
import { getUseListOrder } from "./hook/ListOrder";
import useCommonListFunctions from "../../hooks/useCommonList/useCommonListFunctions";
import { AdminOrderRevenueSummary, getAdminOrderRevenueSummary } from "../../../services/OrderService";

export const HomeContext = createContext<{
	openDetailDialog: boolean;

	loading: boolean;
	loadMore: () => void;
	isInitLoading: boolean;
	isValidated: boolean | undefined;
} | null>(null);

export default function MyBookingPage() {
	const hash = typeof window !== "undefined" && window ? window.location?.hash : undefined;
	useEffect(() => {
		if (hash === undefined) return;

		const timeout = setTimeout(() => {
			const distance = document.getElementById(hash.replace("#", ""))?.offsetTop || 0;
			window.scrollTo({
				top: distance,
				left: 0,
				behavior: "smooth",
			});
			clearTimeout(timeout);
		}, 300);
	}, [hash]);
	const { user } = useLoadedUser();
	const {
		filterStates,
		filterFromDate,
		filterToDate,
		filterStore,
		filterUserId,
		filterFromReplyDate,
		filterToReplyDate,
		filterCountry,
		filterProvice,
		filterKeyword,
		filterActive,
	} = useCommonListFunctions();
	const [revenueSummary, setRevenueSummary] = useState<AdminOrderRevenueSummary | null>(null);
	const [revenueSummaryLoading, setRevenueSummaryLoading] = useState(false);
	const { listOrder, reload, count, countPages } = getUseListOrder({ user: user });

	useEffect(() => {
		if (user?.role !== UserRole.Admin) {
			setRevenueSummary(null);
			return;
		}

		let cancelled = false;

		const loadRevenueSummary = async () => {
			setRevenueSummaryLoading(true);
			try {
				const startOfDayFromDate = filterFromDate
					? new Date(new Date(filterFromDate).setHours(0, 0, 0, 0))
					: undefined;
				const endOfDayToDate = filterToDate
					? new Date(new Date(filterToDate).setHours(23, 59, 59, 999))
					: undefined;
				const startOfDayReplyFromDate = filterFromReplyDate
					? new Date(new Date(filterFromReplyDate).setHours(0, 0, 0, 0))
					: undefined;
				const endOfDayToReplyDate = filterToReplyDate
					? new Date(new Date(filterToReplyDate).setHours(23, 59, 59, 999))
					: undefined;
				const summary = await getAdminOrderRevenueSummary({
					status: filterStates?.split(";") || undefined,
					fromDate: startOfDayFromDate,
					toDate: endOfDayToDate,
					store: filterStore || undefined,
					userId: filterUserId || undefined,
					fromReplyDate: startOfDayReplyFromDate,
					toReplyDate: endOfDayToReplyDate,
					country: filterCountry || undefined,
					provice: filterProvice || undefined,
					id: filterKeyword || undefined,
					stationActive: filterActive || undefined,
				});

				if (!cancelled) {
					setRevenueSummary(summary);
				}
			} catch (error) {
				if (!cancelled) {
					setRevenueSummary(null);
				}
			} finally {
				if (!cancelled) {
					setRevenueSummaryLoading(false);
				}
			}
		};

		loadRevenueSummary();

		return () => {
			cancelled = true;
		};
	}, [
		user?.role,
		filterStates,
		filterFromDate,
		filterToDate,
		filterStore,
		filterUserId,
		filterFromReplyDate,
		filterToReplyDate,
		filterCountry,
		filterProvice,
		filterKeyword,
		filterActive,
	]);

	return (
		<MyBookingWrapper>
			<MyBookingPageNavigation />
			{user?.role === UserRole.Admin && <BookingFilter reload={reload} countPages={countPages}/>}
			<BookingContent
				listOrder={listOrder}
				reload={reload}
				count={count}
				revenueSummary={revenueSummary}
				revenueSummaryLoading={revenueSummaryLoading}
			/>
		</MyBookingWrapper>
	);
}
